"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.claimOwnerInstructions = exports.vsrPreparationInstructions = exports.THIRTY_DAYS_IN_SECONDS = exports.THIRTY_DAYS = void 0;
const web3_js_1 = require("@solana/web3.js");
const sdk_1 = require("./sdk");
const api_1 = require("./api");
const solana_spl_token_modern_1 = require("solana-spl-token-modern");
const vsr_client_1 = require("./vsr-client");
const bn_js_1 = __importDefault(require("bn.js"));
const spl_governance_1 = require("@solana/spl-governance");
const claimInstruction_1 = require("./claimInstruction");
exports.THIRTY_DAYS = 30;
exports.THIRTY_DAYS_IN_SECONDS = exports.THIRTY_DAYS * 24 * 60 * 60;
/**
 * Creating instructions to create deposit entry when needed,
 * and to check what is deposit entry when it exists already.
 */
async function vsrPreparationInstructions({ program, vsrProgramId, realm, mint, authority, }) {
    const provider = program.provider;
    const instructions = [];
    // the next step is to deposit the claimed tokens to the VSR program
    // this can be multiple steps:
    //  - we need to have the voter account created
    //  - we need to have the deposit entry created
    //  - when there is existing deposit entry with non-compatible lockup type then trying to merge existing
    const vsrProgram = (0, vsr_client_1.getVsrProgram)({ provider, programId: vsrProgramId });
    let vsrDepositEntry = await getVsrDepositEntry({
        vsrProgram,
        realm,
        mint,
        authority,
    });
    const [registrar] = (0, vsr_client_1.registrarAddress)({
        programId: vsrProgram.programId,
        realm,
        mint,
    });
    const [voter, voterBump] = (0, vsr_client_1.voterAddress)({
        programId: vsrProgram.programId,
        registrar,
        voterAuthority: authority,
    });
    const [voterWeightRecord, voterWeightRecordBump] = (0, vsr_client_1.voterWeightRecordAddress)({
        programId: vsrProgram.programId,
        registrar,
        voterAuthority: authority,
    });
    if (vsrDepositEntry.type === 'NoDepositEntryAvailable') {
        // there is no available entry, let's try to find records that can be merged
        const groupedDepositEntries = groupUsedNonClaimableLockupsByKind(vsrDepositEntry.depositEntries);
        for (const groupedEntries of groupedDepositEntries) {
            if (groupedEntries.length > 1) {
                // we can merge something here; let's merge to the first entry
                const mergeToDepositEntryIndex = groupedEntries[0];
                const mergeFromDepositEntryIndex = groupedEntries[1];
                const amount = // TODO: verify this is correct
                 vsrDepositEntry.depositEntries[mergeFromDepositEntryIndex]
                    .amountDepositedNative;
                instructions.push(await vsrProgram.methods
                    .internalTransferLocked(mergeFromDepositEntryIndex, mergeToDepositEntryIndex, amount)
                    .accountsStrict({
                    registrar,
                    voter,
                    voterAuthority: authority,
                })
                    .instruction());
                instructions.push(await vsrProgram.methods
                    .closeDepositEntry(mergeFromDepositEntryIndex)
                    .accountsStrict({
                    voter,
                    voterAuthority: authority,
                })
                    .instruction());
                // let's merge when we can and only used that entry, not needed to continue for other entries
                vsrDepositEntry = {
                    type: 'CreateEntryAt',
                    index: mergeFromDepositEntryIndex,
                };
                break;
            }
        }
    }
    const vsrDepositEntryIndex = vsrDepositEntry.type === 'NoVoterAccount'
        ? 0 // a new voter account has got all entries available, will us the first one
        : vsrDepositEntry.type === 'CreateEntryAt' ||
            vsrDepositEntry.type === 'DepositIntoExisting'
            ? vsrDepositEntry.index // we can use the existing entry
            : undefined; // not able to find any entry to use ('NoDepositEntryAvailable')
    if (vsrDepositEntryIndex === undefined) {
        throw new Error(`No mint ${mint.toBase58()} deposit entry with constant 30 days type or an empty available or merge-able entry was found` +
            `for authority ${authority.toBase58()} (registrar: ${registrar.toBase58()}, voter: ${voter.toBase58()})`);
    }
    const vsrVoterVault = (0, vsr_client_1.vaultAddress)({ voter, mint });
    if (vsrDepositEntry.type === 'NoVoterAccount' ||
        vsrDepositEntry.type === 'CreateEntryAt') {
        if (vsrDepositEntry.type === 'NoVoterAccount') {
            // no Voter account exists
            const ix = await vsrProgram.methods
                .createVoter(voterBump, voterWeightRecordBump)
                .accounts({
                registrar,
                voter,
                voterAuthority: authority,
                voterWeightRecord: voterWeightRecord,
                payer: provider.wallet.publicKey,
                systemProgram: web3_js_1.SystemProgram.programId,
                rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                instructions: web3_js_1.SYSVAR_INSTRUCTIONS_PUBKEY,
            })
                .instruction();
            instructions.push(ix);
        }
        // the Voter Stake Registry manages token treasury and locks the tokens
        // for user can use the locked tokens for veMNDE voting a TokenOwnerRecord has to exist
        const splGovRealmData = await (0, spl_governance_1.getRealm)(provider.connection, realm);
        const splGovRealmProgramId = splGovRealmData.owner;
        const tokenOwnerInstructions = [];
        const tokenOwnerRecordAddress = await (0, spl_governance_1.withCreateTokenOwnerRecord)(tokenOwnerInstructions, splGovRealmProgramId, spl_governance_1.PROGRAM_VERSION_V3, // TODO: probably could be configurable
        realm, // realm address
        authority, // owner
        mint, // mint
        provider.wallet.publicKey // payer
        );
        if ((await provider.connection.getAccountInfo(tokenOwnerRecordAddress)) ===
            null) {
            // token owner record does not exist, we need to have it for the user may vote
            instructions.push(...tokenOwnerInstructions);
        }
        const ix = await vsrProgram.methods
            // period is 30, as LockupKind.constant has got period_secs set to SECS_PER_DAY
            .createDepositEntry(vsrDepositEntryIndex, { constant: {} }, new bn_js_1.default(0), exports.THIRTY_DAYS, false)
            .accounts({
            registrar,
            voter,
            vault: vsrVoterVault,
            voterAuthority: authority,
            payer: provider.wallet.publicKey,
            depositMint: mint,
            systemProgram: web3_js_1.SystemProgram.programId,
            tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
            associatedTokenProgram: solana_spl_token_modern_1.ASSOCIATED_TOKEN_PROGRAM_ID,
            rent: web3_js_1.SYSVAR_RENT_PUBKEY,
        })
            .instruction();
        instructions.push(ix);
    }
    return {
        vsrProgram,
        instructions,
        registrar,
        voter,
        vault: vsrVoterVault,
        depositEntryIndex: vsrDepositEntryIndex,
    };
}
exports.vsrPreparationInstructions = vsrPreparationInstructions;
/**
 * Generate instruction to claim and deposit. The claim instruction of the contract requires
 * the transaction transfers the claimed tokens to a VSR token account.
 * For that we need to find out deposit entry for the MNDE token of the expected 30 days lockup time,
 *
 * This functions preset all required data and returns composed set of instructions.
 *
 * @type {Object} args - Arguments on instruction creation
 * @param param {IncentivesDistributionProgram} args.program - anchor program instance
 * @param param {PublicKey} args.treasuryAccount - treasury account
 * @param param {PublicKey} args.authority - claim record authority [SIGNER]
 * @type {Object} return - Return instructions
 * @return {TransactionInstruction[]} return.instructions - Instructions to manage claiming and depositing
 */
async function claimOwnerInstructions({ program, authority, mint = vsr_client_1.MNDE_MINT, treasuryAccount = sdk_1.STAKERS_TREASURY, vsrProgramId = vsr_client_1.MNDE_VSR_PROGRAM_ID, realm = vsr_client_1.MNDE_REALM_ADDRESS, }) {
    const provider = program.provider;
    const instructions = [];
    const { tokenStore } = await (0, api_1.treasury)({ program, address: treasuryAccount });
    if (!mint) {
        // eslint-disable-next-line @typescript-eslint/no-extra-semi
        ;
        ({ mint } = await (0, solana_spl_token_modern_1.getAccount)(provider.connection, tokenStore));
    }
    const authorityAta = (0, solana_spl_token_modern_1.getAssociatedTokenAddressSync)(mint, authority, true);
    // when ATA account for the authority does not exist yet, we need to create it to be able to claim&deposit
    try {
        await (0, solana_spl_token_modern_1.getAccount)(provider.connection, authorityAta);
    }
    catch (e) {
        instructions.push((0, solana_spl_token_modern_1.createAssociatedTokenAccountInstruction)(provider.wallet.publicKey, // payer
        authorityAta, authority, // owner
        mint));
    }
    const { vsrProgram, instructions: prepInstructions, depositEntryIndex, vault, voter, registrar, } = await vsrPreparationInstructions({
        program,
        vsrProgramId,
        realm,
        mint,
        authority,
    });
    instructions.push(...prepInstructions);
    const claimingAmount = await (0, api_1.getClaimableAmount)({
        program,
        treasury: treasuryAccount,
        authority,
    });
    // let's claim available tokens from the incentive program to ATA
    const { instruction: claimIx, address: claimRecordAddress } = await (0, claimInstruction_1.claimInstruction)({
        program,
        treasuryAccount,
        tokenStore,
        claimRecordAuthority: authority,
        transferTokenTo: authorityAta,
    });
    instructions.push(claimIx);
    // next instruction has to be depositing the claimed tokens to the VSR
    instructions.push(await vsrProgram.methods
        .deposit(depositEntryIndex, claimingAmount)
        .accounts({
        registrar,
        voter,
        vault,
        depositAuthority: authority,
        tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
        depositToken: authorityAta,
    })
        .instruction());
    return { address: claimRecordAddress, instructions };
}
exports.claimOwnerInstructions = claimOwnerInstructions;
/**
 * Search VSR mango plugin for Voter account deposit entry.
 * @returns {VsrDepositEntryResult} - Returns type 'Existing' with mint entry with 30 days lockup type is found,
 *                                    type 'New' when not proprietary entry is found but there is empty place to create new
 *                                    and type 'NoVoterAccount' when no `Voter` account exists.
 */
async function getVsrDepositEntry({ vsrProgram, realm, mint, authority, }) {
    const registrar = (0, vsr_client_1.registrarAddress)({
        programId: vsrProgram.programId,
        realm,
        mint,
    })[0];
    const voter = (0, vsr_client_1.voterAddress)({
        programId: vsrProgram.programId,
        registrar,
        voterAuthority: authority,
    })[0];
    const registrarData = await vsrProgram.account.registrar.fetchNullable(registrar);
    if (registrarData === null) {
        throw new Error(`Registrar account ${registrar.toBase58()} does not exist ` +
            `(VSR program id: ${vsrProgram.programId.toBase58()}, realm: ${realm.toBase58()}, mint: ${mint.toBase58()}})`);
    }
    const registrarVotingMints = registrarData.votingMints;
    let vsrMintConfigIdx = undefined;
    for (let i = 0; i < registrarVotingMints.length; i++) {
        if (registrarVotingMints[i].mint.equals(mint)) {
            vsrMintConfigIdx = i;
            break;
        }
    }
    if (vsrMintConfigIdx === undefined) {
        throw new Error(`Mint ${mint.toBase58()} is not configured in registrar ${registrar.toBase58()} ' +
      'at realm ${registrarData.realm.toBase58()}`);
    }
    const voterData = await vsrProgram.account.voter.fetchNullable(voter);
    if (voterData === null) {
        return { type: 'NoVoterAccount' };
    }
    // finding deposit entry with defined mint and with constant lockup type with 30 days lockup
    const deposits = voterData.deposits;
    for (let i = 0; i < deposits.length; i++) {
        const { lockup, votingMintConfigIdx, allowClawback, isUsed } = deposits[i];
        if (votingMintConfigIdx === vsrMintConfigIdx &&
            lockup.kind.constant !== undefined &&
            lockup.endTs.sub(lockup.startTs).eqn(exports.THIRTY_DAYS_IN_SECONDS) &&
            !allowClawback &&
            isUsed) {
            return { type: 'DepositIntoExisting', index: i };
        }
        if (!isUsed) {
            //the first available index that's not used will be taken
            return { type: 'CreateEntryAt', index: i };
        }
    }
    // neither found existing entry with matching lockup type, nor non-used non-claimable entry
    return { type: 'NoDepositEntryAvailable', depositEntries: deposits };
}
function groupUsedNonClaimableLockupsByKind(depositEntries) {
    const groupedItems = {};
    for (let i = 0; i < depositEntries.length; i++) {
        const { lockup, votingMintConfigIdx: mintIndex, isUsed, allowClawback, } = depositEntries[i];
        if (!isUsed || allowClawback) {
            // when not used, then not initialized (is closed) and there is nothing we can do with it later
            // when clawback is allowed, then merging is not permitted
            continue;
        }
        const duration = lockup.endTs.sub(lockup.startTs);
        const kind = (0, vsr_client_1.lockupKind)(lockup.kind);
        const key = `${mintIndex}_${kind}_${duration}`;
        if (!groupedItems[key]) {
            groupedItems[key] = [i];
        }
        else {
            groupedItems[key].push(i);
        }
    }
    return Object.values(groupedItems);
}
//# sourceMappingURL=claimOwnerInstructions.js.map